import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-normal leading-tight transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary/60 text-primary-foreground hover:bg-primary/100',
        transparent: 'flex w-1/3 items-center justify-center gap-2 text-sm bg-transparent hover:text-white text-[#6ad2ff] border px-4 rounder-lg border-white hover:bg-indigo-500 text-lg hover:border-indigo-500',
        primary: 'flex w-1/3 items-center justify-center gap-2 bg-[#3F8CFF] border-[#3F8CFF] text-white border px-4 rounder-lg hover:bg-grey-dark text-lg hover:border-indigo-500',
        success: 'flex w-1/3 items-center justify-center gap-2 bg-[#27B586] border-[#27B586] text-white border px-4 rounder-lg hover:bg-grey-dark text-lg hover:border-indigo-500',
        warning: 'flex w-1/3 items-center justify-center gap-2 bg-[#F08636] border-[#F08636] text-white border px-4 rounder-lg hover:bg-grey-dark text-lg hover:border-indigo-500',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-primary/25 hover:text-accent-foreground',
        secondary: 'bg-primary/40 text-secondary-foreground hover:bg-primary/60',
        ghost: 'font-normal text-primary hover:bg-primary/25',
        link: 'font-normal text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-7 px-2 py-2',
        sm: 'h-6 rounded px-2',
        lg: 'h-9 rounded px-2',
        icon: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref}{...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
